import React from 'react';
import { FaArrowLeft, FaShieldAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';

function Privacy() {
    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #050a14 0%, #0a1628 100%)',
            color: '#ffffff',
            fontFamily: "'Inter', -apple-system, sans-serif",
            position: 'relative',
        }}>
            {/* Grid overlay */}
            <div style={{
                position: 'fixed',
                inset: 0,
                backgroundImage:
                    'linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px)',
                backgroundSize: '60px 60px',
                pointerEvents: 'none',
                zIndex: 0,
            }} />

            <div style={{
                position: 'relative',
                zIndex: 1,
                maxWidth: '820px',
                margin: '0 auto',
                padding: '60px 24px 80px',
            }}>
                {/* Back link */}
                <Link to="/" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#00d4ff',
                    textDecoration: 'none',
                    marginBottom: '40px',
                    fontWeight: '600',
                    fontSize: '0.88rem',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    transition: 'opacity 0.2s',
                }}>
                    <FaArrowLeft /> Back to Home
                </Link>

                {/* Header */}
                <div style={{ marginBottom: '48px' }}>
                    <p style={{
                        color: '#00d4ff',
                        fontSize: '0.75rem',
                        fontFamily: "'Orbitron', sans-serif",
                        fontWeight: '700',
                        letterSpacing: '5px',
                        textTransform: 'uppercase',
                        marginBottom: '12px',
                    }}>
                        Legal Document
                    </p>
                    <h1 style={{
                        fontFamily: "'Orbitron', sans-serif",
                        fontSize: '2.4rem',
                        fontWeight: '900',
                        background: 'linear-gradient(90deg, #ffffff 40%, #00d4ff)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: '12px',
                        lineHeight: '1.2',
                    }}>
                        <FaShieldAlt style={{ WebkitTextFillColor: '#00d4ff', fontSize: '1.8rem', verticalAlign: 'middle', marginRight: '14px' }} />
                        Privacy Policy
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', letterSpacing: '1px' }}>
                        Last Updated: February 3, 2026
                    </p>
                    <div style={{
                        width: '80px',
                        height: '2px',
                        background: 'linear-gradient(90deg, #00d4ff, #E31837)',
                        marginTop: '20px',
                    }} />
                </div>


                {/* Content */}
                <div style={{ lineHeight: '1.85', color: 'rgba(255,255,255,0.82)' }}>
                    <p style={{ marginBottom: '24px', fontSize: '1rem' }}>
                        <strong style={{ color: '#ffffff' }}>StopNext Services (Pvt) Ltd</strong> ("we," "our," or "us") operates the{' '}
                        <strong style={{ color: '#00d4ff' }}>BookTruck</strong> mobile application (the "App"). This page informs you of our
                        policies regarding the collection, use, and disclosure of personal data when you use our App and the choices you have
                        associated with that data.
                    </p>
                    <p style={{ marginBottom: '32px' }}>
                        By using the App, you agree to the collection and use of information in accordance with this policy.
                    </p>

                    <Section title="1. Information We Collect" />
                    <p style={{ marginBottom: '20px' }}>
                        To provide our truck booking and logistics services, we collect several different types of information for various purposes.
                    </p>

                    <SubSection title="1.1 Personal Information" />
                    <p style={{ marginBottom: '12px' }}>When you register as a user or driver, we may ask you to provide:</p>
                    <PolicyList items={['Full Name', 'Phone Number', 'Email Address', 'Physical Address']} />

                    <SubSection title="1.2 Sensitive Personal Information (Driver Verification)" />
                    <p style={{ marginBottom: '12px' }}>
                        If you register as a Driver, we require specific documents to verify your identity and the legality of your vehicle.
                        This includes uploading images of:
                    </p>
                    <PolicyList items={[
                        <><strong style={{ color: '#ffffff' }}>National ID Card:</strong> To verify your legal identity.</>,
                        <><strong style={{ color: '#ffffff' }}>Driver's License:</strong> To confirm you are legally permitted to operate the vehicle.</>,
                        <><strong style={{ color: '#ffffff' }}>Truck Certificate/Registration:</strong> To verify the vehicle is registered and roadworthy.</>,
                        <><strong style={{ color: '#ffffff' }}>Transit Permits:</strong> To ensure compliance with local transportation laws.</>,
                    ]} />
                    <p style={{ marginBottom: '32px' }}>
                        <strong style={{ color: '#00d4ff' }}>How we use this sensitive data:</strong> These documents are strictly used for
                        verification purposes only. They are reviewed by our internal admin team and stored securely. They are never shared
                        with third-party advertisers or sold to data brokers.
                    </p>

                    <SubSection title="1.3 Location Data" />
                    <p style={{ marginBottom: '12px' }}>We may collect your location (GPS and network signals) to:</p>
                    <PolicyList items={[
                        'Track shipments in real-time.',
                        'Match drivers with nearby booking requests.',
                        'Calculate shipping costs and distances.',
                    ]} />
                    <p style={{ marginBottom: '32px' }}>
                        You can enable or disable location services at any time via your device settings.
                    </p>

                    <Section title="2. How We Use Your Data" />
                    <PolicyList items={[
                        <><strong style={{ color: '#ffffff' }}>To provide and maintain the Service:</strong> Facilitating truck bookings and driver matching.</>,
                        <><strong style={{ color: '#ffffff' }}>To notify you about changes:</strong> Service updates or booking statuses.</>,
                        <><strong style={{ color: '#ffffff' }}>To provide customer support:</strong> Resolving issues with bookings.</>,
                        <><strong style={{ color: '#ffffff' }}>To verify Driver accounts:</strong> Ensuring safety and trust on our platform.</>,
                        <><strong style={{ color: '#ffffff' }}>To monitor usage:</strong> Analyzing how the app is used to improve performance.</>,
                    ]} />

                    <Section title="3. Data Storage and Security" />
                    <p style={{ marginBottom: '32px' }}>
                        Your data, including document images, is stored on secure servers hosted on{' '}
                        <strong style={{ color: '#ffffff' }}>Oracle Cloud</strong>. We employ industry-standard security measures
                        (including HTTPS encryption) to protect your personal information from unauthorized access, alteration, or destruction.
                    </p>

                    <Section title="4. Service Providers" />
                    <p style={{ marginBottom: '12px' }}>We may employ third-party companies to facilitate our Service, such as:</p>
                    <PolicyList items={[
                        <><strong style={{ color: '#ffffff' }}>Google Analytics / Firebase:</strong> To assist in analyzing how our Service is used.</>,
                        <><strong style={{ color: '#ffffff' }}>Zoho Mail:</strong> For sending system emails.</>,
                    ]} />
                    <p style={{ marginBottom: '32px' }}>
                        These third parties have access to your Personal Data only to perform tasks on our behalf and are obligated not to
                        disclose or use it for any other purpose.
                    </p>

                    <Section title="5. Data Retention and Deletion" />
                    <p style={{ marginBottom: '16px' }}>
                        We will retain your Personal Data only for as long as necessary for the purposes set out in this Privacy Policy.
                    </p>
                    <p style={{ marginBottom: '16px' }}>
                        <strong style={{ color: '#00d4ff' }}>Account Deletion:</strong> To delete your account and all associated data,
                        contact us at{' '}
                        <a href="mailto:info@stopnextservices.co.zw" style={{ color: '#00d4ff', textDecoration: 'underline' }}>
                            info@stopnextservices.co.zw
                        </a>{' '}
                        or use the delete account feature in the App settings.
                    </p>
                    <p style={{ marginBottom: '32px' }}>
                        Upon request, we will permanently delete your personal information from our active databases, except where required
                        for legal or accounting purposes.
                    </p>

                    <Section title="6. Changes to This Privacy Policy" />
                    <p style={{ marginBottom: '32px' }}>
                        We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy
                        Policy on this page and updating the effective date at the top.
                    </p>

                    <Section title="7. Contact Us" />
                    <p style={{ marginBottom: '12px' }}>If you have any questions about this Privacy Policy, please contact us:</p>
                    <PolicyList items={[
                        <>By email:{' '}<a href="mailto:info@stopnextservices.co.zw" style={{ color: '#00d4ff', textDecoration: 'underline' }}>info@stopnextservices.co.zw</a></>,
                        <>By visiting:{' '}<a href="https://stopnextservices.co.zw" style={{ color: '#00d4ff', textDecoration: 'underline' }}>stopnextservices.co.zw</a></>,
                    ]} />
                </div>
            </div>
        </div>
    );
}

function Section({ title }) {
    return (
        <h2 style={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: '1rem',
            fontWeight: '700',
            color: '#ffffff',
            letterSpacing: '1px',
            marginTop: '40px',
            marginBottom: '16px',
            paddingBottom: '10px',
            borderBottom: '1px solid rgba(0,212,255,0.2)',
        }}>
            {title}
        </h2>
    );
}

function SubSection({ title }) {
    return (
        <h3 style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '0.92rem',
            fontWeight: '700',
            color: '#00d4ff',
            letterSpacing: '0.5px',
            marginTop: '24px',
            marginBottom: '10px',
        }}>
            {title}
        </h3>
    );
}

function PolicyList({ items }) {
    return (
        <ul style={{ paddingLeft: '0', marginBottom: '24px', listStyle: 'none' }}>
            {items.map((item, i) => (
                <li key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    marginBottom: '10px',
                    color: 'rgba(255,255,255,0.78)',
                    fontSize: '0.95rem',
                    lineHeight: '1.7',
                }}>
                    <span style={{
                        color: '#E31837',
                        marginTop: '6px',
                        flexShrink: 0,
                        fontSize: '6px',
                        background: '#E31837',
                        borderRadius: '50%',
                        width: '6px',
                        height: '6px',
                        display: 'inline-block',
                    }} />
                    {item}
                </li>
            ))}
        </ul>
    );
}

export default Privacy;
